import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useNavigate,
  useLocation,
  useBlocker,
  UNSAFE_DataRouterContext,
} from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { FiChevronLeft, FiChevronRight, FiX, FiSliders } from 'react-icons/fi';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import baseline from '../../app-ui/baseline';
import { musicEditionCreateLabel } from './generated/branding';
import { EditionArtwork, artworkImageUris } from './generated/EditionArtwork';
import { selectEditionAlbums } from './generated/model';
import { getLocalizedTasteGenre } from '../taste/generated/localization';
import { exportArtworkPng, downloadArtwork } from './exportArtwork';
import useEditionMotion from './useEditionMotion';
import useEditionData from './useEditionData';
import EditionCustomize from './EditionCustomize';
import EditionLibrary from './EditionLibrary';
import { printArtwork } from './printArtwork';
import {
  customization,
  defaults,
  restoreSettings,
  settingsKey,
  scenes,
  sceneTitles,
  selectionChanged,
  selectionReset,
  filterGroups,
  filterValues,
} from './editorModel';
import PageLayout from '../../components/layout/PageLayout';
import './MusicEdition.css';
export function editionStyle(mode) {
  const c = baseline.tokens[mode === 'dark' ? 'darkColors' : 'lightColors'];
  return {
    '--edition-text': c.text,
    '--edition-bg': c.background,
    '--edition-surface': c.surface,
    '--edition-soft': c.surfaceMuted,
    '--edition-muted': c.textSecondary,
    '--edition-border': c.divider,
  };
}
function RouteGuard({ state, confirm }) {
  const blocker = useBlocker(
    () => !state.current.released && (state.current.dirty || state.current.busy),
  );
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (state.current.busy) blocker.reset();
      else
        confirm({
          cancel: () => blocker.reset(),
          run: () => {
            state.current.released = true;
            blocker.proceed();
          },
        });
    }
  }, [blocker, state, confirm]);
  return null;
}
export default function MusicEditionPage() {
  const user = useUserStore((s) => s.user),
    { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme(),
    navigate = useNavigate();
  return (
    <PageLayout paddingTop={{ xs: '0px', md: '16px' }}>
      {user?.id ? (
        <Editor key={`${user.id}:${user.nickname}`} user={user} />
      ) : (
        <section className="pp-edition" style={editionStyle(resolvedMode)}>
          <header className="pp-edition-header">
            <button
              aria-label={locale === 'en' ? 'Back' : '뒤로'}
              onClick={() => navigate('/mypage')}
            >
              <FiChevronLeft size={22} />
            </button>
            <h1>{musicEditionCreateLabel[locale]}</h1>
            <span />
          </header>
          <p>{locale === 'en' ? 'Please log in to continue.' : '로그인이 필요해요.'}</p>
          <Link to="/login?redirectUrl=%2Fmypage%2Farchive">
            {locale === 'en' ? 'Log in' : '로그인'}
          </Link>
        </section>
      )}
    </PageLayout>
  );
}
function Editor({ user }) {
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme(),
    en = locale === 'en',
    copy = (ko, english) => (en ? english : ko),
    style = editionStyle(resolvedMode),
    navigate = useNavigate(),
    location = useLocation();
  const storageKey = settingsKey(window.location.origin, user.id);
  const [initial] = useState(() => {
    try {
      return { p: restoreSettings(window.localStorage.getItem(storageKey), en) };
    } catch {
      return { p: defaults(en), error: true };
    }
  });
  const [p, setP] = useState(initial.p),
    [saved, setSaved] = useState(customization(initial.p)),
    [scene, setScene] = useState('card'),
    [active, setActive] = useState(null),
    [sheetBaseline, setSheetBaseline] = useState(null),
    [draft, setDraft] = useState({}),
    [confirm, setConfirm] = useState(null),
    [notice, setNotice] = useState(
      initial.error ? copy('저장된 꾸미기를 읽지 못했어요.', 'Could not load saved settings.') : '',
    ),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(0),
    [loaded, setLoaded] = useState({ key: '', uris: new Set() }),
    [slow, setSlow] = useState(false),
    [width, setWidth] = useState(360);
  const live = useRef(true),
    lock = useRef(false),
    request = useRef(),
    guard = useRef({}),
    stage = useRef(),
    touch = useRef(),
    router = useContext(UNSAFE_DataRouterContext);
  const current = customization(p),
    dirty = JSON.stringify(current) !== JSON.stringify(saved);
  guard.current = { ...guard.current, dirty, busy };
  const sameAccount = () => {
    const u = useUserStore.getState().user;
    return String(u?.id) === String(user.id) && u?.nickname === user.nickname;
  };
  useEffect(() => {
    live.current = true;
    const abort = () => {
      if (document.hidden) request.current?.abort();
    };
    document.addEventListener('visibilitychange', abort);
    return () => {
      live.current = false;
      request.current?.abort();
      document.removeEventListener('visibilitychange', abort);
    };
  }, []);
  useEffect(() => {
    const stop = (e) => {
      if (!guard.current.released && (guard.current.dirty || guard.current.busy)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', stop);
    return () => window.removeEventListener('beforeunload', stop);
  }, []);
  const data = useEditionData(user, p, locale),
    { edition } = data;
  const albums = useMemo(
    () => selectEditionAlbums(edition.records, p.selectedIds),
    [edition.records, p.selectedIds],
  );
  const quote = edition.reviews.find((r) => r.review.id === p.quoteId) ?? edition.reviews[0];
  useEffect(() => {
    if (
      data.records.isSuccess &&
      p.quoteId !== null &&
      !edition.reviews.some((r) => r.review.id === p.quoteId)
    )
      setP((v) => ({ ...v, quoteId: null, quoteStart: 0 }));
  }, [data.records.isSuccess, edition.reviews, p.quoteId]);
  const now = new Date(data.asOf.getTime() + 9 * 3600000),
    periodLabel =
      p.period === 'all'
        ? copy('전체 기록', 'ALL RECORDS')
        : p.period === 'year'
          ? String(now.getUTCFullYear())
          : `${now.getUTCFullYear()}.${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  const emptyReason =
    scene === 'persona' && !data.persona
      ? p.period !== 'all'
        ? copy(
            '취향 유형은 전체 기간의 기록을 기준으로 분석해요. 기간을 전체로 바꿔주세요.',
            'Taste types use all-time records. Choose All time.',
          )
        : data.taste.isError
          ? copy(
              '취향 분석을 불러오지 못했어요. 다시 시도해주세요.',
              'Could not load your taste analysis. Please retry.',
            )
          : data.taste.isFetching
            ? copy('취향 분석을 불러오고 있어요.', 'Loading your taste analysis.')
            : undefined
      : scene === 'duet' && data.friend.isError
        ? copy(
            '선택한 회원의 공개 기록을 불러오지 못했어요. 꾸미기에서 회원을 다시 선택해주세요.',
            'Could not load this member. Choose a member again in Customize.',
          )
        : undefined;
  const artwork = {
    ...p,
    edition,
    scene,
    width,
    nickname: user.nickname,
    english: en,
    periodLabel,
    albums,
    quote,
    persona: data.persona,
    comparison: data.comparison,
    artistImages: data.artistQuery.data,
    festivalLayout: 'names',
    genreLabel: (g) => getLocalizedTasteGenre(g, locale),
    emptyReason,
    showCovers: true,
  };
  const uris = artworkImageUris(artwork),
    imageKey = JSON.stringify([scene, p.format, p.template, p.theme, revision, uris]);
  const imageReady =
    (!uris.length || (loaded.key === imageKey && uris.every((u) => loaded.uris.has(u)))) &&
    !(scene === 'festival' && data.artistQuery.isFetching);
  const refreshing =
    data.records.isFetching ||
    (scene === 'persona' && data.taste.isFetching) ||
    (scene === 'duet' && data.friend.isFetching);
  const motion = useEditionMotion(
    scene,
    busy || p.theme === 'artwork' || scene === 'genres' || scene === 'persona',
    data.records.dataUpdatedAt,
  );
  const activeKey = useRef(imageKey);
  activeKey.current = imageKey;
  useEffect(() => {
    setSlow(false);
    const t = setTimeout(() => setSlow(true), 10000);
    return () => clearTimeout(t);
  }, [imageKey]);
  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    const measure = () => setWidth(element.getBoundingClientRect().width);
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(element);
    return () => obs.disconnect();
  }, [data.records.isSuccess]);
  const set = (next) => {
    if (!lock.current) setP((v) => ({ ...v, ...next }));
  };
  const changeFilters = (input) => {
    const values = filterValues(input);
    if (lock.current) return;
    const apply = () => {
      if (values.period && values.period !== p.period) {
        setP((v) => ({ ...v, ...values, ...selectionReset }));
        setSaved((v) => ({ ...v, ...selectionReset }));
        setScene(values.scene && values.scene !== scene ? values.scene : 'card');
      } else {
        setP((v) => ({ ...v, ...values }));
        if (values.scene) setScene(values.scene);
      }
      setActive(null);
    };
    if (values.period && values.period !== p.period && selectionChanged(p, saved))
      setConfirm({ run: apply });
    else apply();
  };
  const open = (id) => {
    if (lock.current) return;
    setNotice('');
    if (id === 'customize') setSheetBaseline(current);
    if (id === 'all') setDraft({ ...p, scene });
    setActive(id);
  };
  const close = () => {
    if (lock.current) return;
    if (active === 'customize' && JSON.stringify(current) !== JSON.stringify(sheetBaseline))
      setConfirm({
        run: () => {
          setP((v) => ({ ...v, ...sheetBaseline }));
          setActive(null);
        },
      });
    else setActive(null);
  };
  const back = () => {
    if (lock.current) return;
    const run = () => {
      guard.current.released = true;
      if (location.state?.editionFromMy) navigate(-1);
      else navigate('/mypage');
    };
    if (dirty) setConfirm({ run });
    else run();
  };
  const save = () => {
    if (lock.current || !sameAccount()) return;
    lock.current = true;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...p,
          version: 1,
          showCovers: true,
          headliner: null,
          selectedWords: undefined,
        }),
      );
      setSaved(current);
      setSheetBaseline(current);
      guard.current.dirty = false;
      setActive(null);
      setNotice(copy('꾸미기를 저장했어요.', 'Settings saved.'));
    } catch {
      setNotice(
        copy(
          '꾸미기 저장에 실패했어요. 입력한 설정은 유지됩니다.',
          'Could not save settings. Your edits have been kept.',
        ),
      );
    } finally {
      lock.current = false;
    }
  };
  const exportImage = async (printing = false) => {
    if (printing && p.format !== 'card') {
      set({ format: 'card' });
      return;
    }
    if (lock.current || !edition.count || !imageReady || refreshing || !sameAccount()) return;
    lock.current = true;
    setBusy(true);
    setNotice('');
    const controller = new AbortController();
    request.current = controller;
    try {
      const result = await exportArtworkPng(
        { ...artwork, motionProgress: 1, animateCharts: false },
        { signal: controller.signal },
      );
      if (live.current && sameAccount() && !controller.signal.aborted && !document.hidden) {
        const blob = printing
          ? await printArtwork(result.blob, {
              nickname: user.nickname,
              showName: p.showName,
              siteUrl: process.env.REACT_APP_SITE_URL || 'https://postpop.kr',
              signal: controller.signal,
            })
          : result.blob;
        if (!live.current || !sameAccount() || controller.signal.aborted || document.hidden) return;
        downloadArtwork(blob, `postpop-${scene}-${p.format}.${printing ? 'pdf' : 'png'}`);
        setNotice(
          printing
            ? copy('인쇄 PDF를 저장했어요.', 'Print PDF saved.')
            : copy('이미지를 저장했어요.', 'Image saved.'),
        );
      }
    } catch {
      if (live.current && sameAccount())
        setNotice(
          copy(
            controller.signal.aborted
              ? '이미지 저장을 취소했어요. 입력한 설정은 유지됩니다.'
              : '이미지를 저장하지 못했어요. 잠시 후 다시 시도해주세요. 입력한 설정은 유지됩니다.',
            controller.signal.aborted
              ? 'Image export cancelled. Your edits have been kept.'
              : 'Could not save image. Try again. Your selections have been kept.',
          ),
        );
    } finally {
      lock.current = false;
      if (live.current) setBusy(false);
    }
  };
  const index = scenes.indexOf(scene),
    turn = (direction) => {
      if (!lock.current)
        setScene(scenes[Math.max(0, Math.min(scenes.length - 1, index + direction))]);
    };
  const groups = filterGroups(copy),
    title = active === 'all' ? copy('필터', 'Filters') : groups.find((g) => g.id === active)?.label;
  const routeConfirm = useCallback((value) => setConfirm(value), []);
  return (
    <section className="pp-edition" style={style}>
      {router && <RouteGuard state={guard} confirm={routeConfirm} />}
      <header className="pp-edition-header">
        <button onClick={back} disabled={busy} aria-label={copy('뒤로', 'Back')}>
          <FiChevronLeft size={22} />
        </button>
        <h1>{musicEditionCreateLabel[locale]}</h1>
        <span />
      </header>
      {data.records.isPending ? (
        <p role="status">{copy('기록을 불러오고 있어요.', 'Loading your records.')}</p>
      ) : data.records.isError ? (
        <div role="alert">
          <p>{copy('기록을 불러오지 못했어요.', 'Could not load your records.')}</p>
          <button onClick={() => data.records.refetch()}>{copy('다시 시도', 'Retry')}</button>
        </div>
      ) : (
        <>
          <p className="pp-edition-intro">
            {copy(
              '내 음악 취향과 기록을 카드로.\n나만의 아카이브를 공유해보세요.',
              'Your music taste, made into cards.\nShare your own archive.',
            )}
          </p>
          <nav className="pp-edition-filters" aria-label={copy('아카이브 필터', 'Archive filters')}>
            <button
              onClick={() => open('all')}
              disabled={busy}
              aria-label={copy('전체 필터', 'All filters')}
            >
              <FiSliders size={18} />
            </button>
            {groups.map((g) => (
              <button key={g.id} disabled={busy} onClick={() => open(g.id)} aria-label={g.label}>
                {g.options.find(([v]) => v === (g.id === 'scene' ? scene : p[g.id]))?.[1] ??
                  g.label}
                <FiChevronRight size={13} />
              </button>
            ))}
          </nav>
          <div className="pp-edition-composition">
            <div className="pp-edition-preview-column">
              <div
                ref={stage}
                className="pp-edition-stage"
                onTouchStart={(e) => {
                  const t = e.touches[0];
                  touch.current =
                    t.clientX < 32 ? null : { x: t.clientX, y: t.clientY, time: Date.now() };
                }}
                onTouchEnd={(e) => {
                  const start = touch.current;
                  touch.current = null;
                  if (!start) return;
                  const t = e.changedTouches[0],
                    dx = t.clientX - start.x,
                    dy = t.clientY - start.y;
                  if (
                    Math.abs(dy) < 18 &&
                    (Math.abs(dx) > 48 ||
                      (Math.abs(dx) > 12 &&
                        Math.abs(dx) / Math.max(1, Date.now() - start.time) > 0.55))
                  )
                    turn(dx < 0 ? 1 : -1);
                }}
              >
                <EditionArtwork
                  {...artwork}
                  key={imageKey}
                  motionProgress={motion}
                  animateCharts={!busy && imageReady && p.theme !== 'artwork'}
                  onImageLoad={(uri) => {
                    if (activeKey.current === imageKey)
                      setLoaded((old) =>
                        old.key === imageKey && old.uris.has(uri)
                          ? old
                          : {
                              key: imageKey,
                              uris: new Set([...(old.key === imageKey ? old.uris : []), uri]),
                            },
                      );
                  }}
                />
              </div>
              <div className="pp-edition-navigation">
                <button
                  aria-label={copy('이전 카드', 'Previous card')}
                  disabled={busy || index === 0}
                  onClick={() => turn(-1)}
                >
                  <FiChevronLeft size={22} />
                </button>
                <p aria-live="polite">
                  {String(index + 1).padStart(2, '0')} / 13 · {copy(...sceneTitles[index])}
                </p>
                <button
                  aria-label={copy('다음 카드', 'Next card')}
                  disabled={busy || index === 12}
                  onClick={() => turn(1)}
                >
                  <FiChevronRight size={22} />
                </button>
              </div>
            </div>
            <div className="pp-edition-tools">
              {!!edition.count && (
                <>
                  {scene === 'quiz' && edition.scenes.includes('quiz') && (
                    <button
                      onClick={() =>
                        setNotice(
                          copy('내 만점 앨범: ', 'My five-star album: ') +
                            edition.perfect[0]?.album.title,
                        )
                      }
                    >
                      {copy('정답 확인 (이미지에 미포함)', 'Reveal answer (not on image)')}
                    </button>
                  )}
                  {!imageReady && (
                    <p role="status">
                      {slow
                        ? copy(
                            '일부 커버를 불러오지 못했어요. 아래에서 다시 불러와주세요.',
                            'Some covers are unavailable. Retry loading them below.',
                          )
                        : copy('공유할 커버 이미지를 준비하고 있어요…', 'Preparing cover images…')}
                    </p>
                  )}
                  {!imageReady && slow && (
                    <button
                      onClick={() => {
                        setLoaded({ key: '', uris: new Set() });
                        setRevision((v) => v + 1);
                      }}
                    >
                      {copy('커버 다시 불러오기', 'Retry covers')}
                    </button>
                  )}
                  {scene === 'persona' && data.taste.isError && (
                    <button onClick={() => data.taste.refetch()}>
                      {copy('취향 분석 다시 불러오기', 'Retry taste analysis')}
                    </button>
                  )}
                  <button
                    className="pp-edition-primary"
                    disabled={busy || !imageReady || refreshing}
                    onClick={() => exportImage()}
                  >
                    {busy
                      ? copy('이미지를 준비하고 있어요…', 'Preparing image…')
                      : copy('이 이미지 저장', 'Save this image')}
                  </button>
                  <button
                    disabled={busy || !imageReady || refreshing}
                    onClick={() => exportImage(true)}
                  >
                    {p.format === 'card'
                      ? copy('앞·뒷면 인쇄 PDF', 'Print front + back PDF')
                      : copy('인쇄용 소장 카드로 전환', 'Switch to printable pocket card')}
                  </button>
                  <small>
                    {copy(
                      '소장 카드 형식에서 사용합니다. A4 한 장에 63×88mm 앞·뒷면과 재단선을 배치합니다. 100% 크기로 인쇄해 잘라 붙여주세요. 닉네임을 숨기면 프로필 QR도 제외합니다.',
                      'Use pocket-card format. An A4 sheet holds separate 63×88mm front/back faces with crop marks. Print at 100%, cut and join. Hiding your name also removes the profile QR.',
                    )}
                  </small>
                  {busy && busy !== 'archive' && (
                    <button onClick={() => request.current?.abort()}>
                      {copy('저장 취소', 'Cancel export')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
      {!active && notice && <p role="status">{notice}</p>}
      <EditionLibrary
        user={user}
        artwork={artwork}
        data={data}
        locale={locale}
        copy={copy}
        style={style}
        busy={busy}
        setBusy={setBusy}
        lock={lock}
        request={request}
        live={live}
        sameAccount={sameAccount}
        imageReady={imageReady}
        refreshing={refreshing}
        notice={setNotice}
      />
      <Dialog
        open={!!active}
        onClose={close}
        maxWidth="sm"
        fullWidth
        className="pp-edition-dialog"
        PaperProps={{ className: 'pp-edition', style }}
        aria-labelledby="edition-dialog-title"
      >
        <DialogTitle component="div">
          <div className="pp-edition-dialog-title">
            <h2 id="edition-dialog-title">{title}</h2>
            <button aria-label={copy('닫기', 'Close')} onClick={close} disabled={busy}>
              <FiX size={22} />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          {active === 'customize' ? (
            <EditionCustomize
              key="customize"
              p={p}
              set={set}
              albums={albums}
              edition={edition}
              quote={quote}
              data={{ ...data, nickname: user.nickname, jump: setScene }}
              copy={copy}
              save={save}
              busy={busy}
              notice={setNotice}
            />
          ) : (
            <div className="pp-edition-options">
              {groups
                .filter((g) => active === 'all' || g.id === active)
                .map((g) => (
                  <div className="pp-edition-filter-group" key={g.id}>
                    {active === 'all' && <h3>{g.label}</h3>}
                    {g.id === 'customize' ? (
                      <button onClick={() => open('customize')}>{g.label}</button>
                    ) : (
                      g.options.map(([value, label]) => (
                        <button
                          key={value}
                          aria-pressed={
                            (active === 'all'
                              ? draft[g.id]
                              : g.id === 'scene'
                                ? scene
                                : p[g.id]) === value
                          }
                          onClick={() =>
                            active === 'all'
                              ? setDraft((v) => ({ ...v, [g.id]: value }))
                              : changeFilters({ [g.id]: value })
                          }
                        >
                          {label}
                        </button>
                      ))
                    )}
                  </div>
                ))}
            </div>
          )}
          {notice && <p role="status">{notice}</p>}
        </DialogContent>
        <DialogActions>
          <button
            onClick={() =>
              active === 'all'
                ? setDraft({ ...defaults(en), scene: 'card' })
                : setConfirm({ run: () => setP((v) => ({ ...v, ...customization(defaults(en)) })) })
            }
            disabled={busy || !['all', 'customize'].includes(active)}
          >
            {copy('초기화', 'Reset')}
          </button>
          <button
            onClick={() =>
              active === 'customize' ? save() : active === 'all' ? changeFilters(draft) : close()
            }
            disabled={busy}
          >
            {copy('완료', 'Done')}
          </button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!confirm}
        onClose={() => {
          confirm?.cancel?.();
          setConfirm(null);
        }}
        PaperProps={{ className: 'pp-edition', style }}
        aria-labelledby="edition-discard-title"
      >
        <DialogTitle id="edition-discard-title">
          {copy('변경 내용을 저장하지 않고 나갈까요?', 'Discard unsaved changes?')}
        </DialogTitle>
        <DialogContent>
          {copy('저장하지 않은 꾸미기는 사라집니다.', 'Your unsaved edits will be lost.')}
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => {
              confirm?.cancel?.();
              setConfirm(null);
            }}
          >
            {copy('계속 수정', 'Keep editing')}
          </button>
          <button
            onClick={() => {
              const run = confirm?.run;
              setConfirm(null);
              run?.();
            }}
          >
            {copy('나가기', 'Discard')}
          </button>
        </DialogActions>
      </Dialog>
    </section>
  );
}
